import React, { useContext, useState } from 'react'
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Button, Form, Popup } from 'semantic-ui-react'
import { CO2El } from '../../../../style'
import { CO2OptionsType } from '~/api/services/co2'
import AssignFeedPage from './AssignFeedPage'
import { FeedPageContext } from '../../..'
import EditableLabelInput from '~/components/EditableLabelInput'
import AddNewGroup from './AddNewGroup'
import { formatNumber } from '~/utils/helpers'
import Phases from './Phases'
import AssignPhasesModal from './AssignPhasesModal'
interface Props {
  milkOptions: CO2OptionsType
}

const FeedGroups: React.FC<Props> = ({ milkOptions }) => {
  const { t } = useTranslation()
  const { feedPage } = useContext(FeedPageContext)

  const { control, setValue } = useFormContext()

  const [isFeedGroupModalOpen, setIsFeedGroupModalOpen] = useState(false)

  const watchCalvingPeriod = useWatch({ control, name: 'calvingPeriod' })
  const watchCowsNumber = useWatch({ control, name: 'cowsNumber' })

  const { fields, append, update, remove } = useFieldArray({
    control,
    name: 'feedGroups',
    keyName: 'key'
  })

  const feedGroups = useWatch({ control, name: 'feedGroups' })

  function formatNumberToInt(number) {
    return parseInt(formatNumber(number, 0).replace(',', '.'))
  }

  function addNewFoodGroup(data) {
    setIsFeedGroupModalOpen(false)
    const days = getDaysFromCowsNumber(data.cowsNumber)
    append({
      ...data,
      days,
      disabled: false,
      coarseFeeds: [],
      concentratedFeeds: [],
      juicyFeeds: []
    })
    //trockenster reduce if type trockenster - value 1
    // laktiriende reduce if type laktiende - value 2
    const index = data.type === 1 ? 0 : 1
    update(index, { ...fields[index], days: fields[index]['days'] - days })
  }

  function getDaysFromCowsNumber(cowsNumber) {
    const calvingDays = formatNumberToInt(watchCalvingPeriod)
    const totalCowsNumber = parseInt(watchCowsNumber.toString().replace(',', '.'))
    const feedGroupCowsNumber = cowsNumber === '' ? 0 : parseInt(cowsNumber)
    return feedGroupCowsNumber * calvingDays / totalCowsNumber
  }

  function handleDeleteFeedGroup(index) {
    const feedGroup = feedGroups[index]
    const fixedFeedGroupIndex = feedGroup?.type && feedGroup?.type === 1 ? 0 : 1
    setValue(`feedGroups[${fixedFeedGroupIndex}].days`, feedGroups[fixedFeedGroupIndex].days + feedGroup.days)
    remove(index)
  }

  function renderFeedGroups() {
    return <>
      <div style={{ width: 'fit-content' }}>
        <div className='subSection'>
          <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginBottom: 5 }}>
            <div>{t('Performance groups')}</div>
            <Popup
              content={t('Add new performance group')}
              trigger={<Button
                icon='plus'
                disabled={fields.length === 4}
                type='button'
                style={{ color: 'white', fontSize: '0.8em' }}
                onClick={() => setIsFeedGroupModalOpen(true)}
              />}
            />
          </div>
        </div>
        <CO2El.StyledDivider />
      </div>
      <Form.Group widths={'equal'} style={{ gap: 20 }}>
        {feedGroups.map((feedGroup, index) => {
          const indexType = feedGroup?.type === 1 ? 0 : 1
          const cowsNrInRelToDaysGroup = parseInt(watchCowsNumber) / parseInt(watchCalvingPeriod) * feedGroup['days']
          const cowPercentage = formatNumber(cowsNrInRelToDaysGroup / parseInt(watchCowsNumber) * 100, indexType)
          return <EditableLabelInput
            key={feedGroup?.id || feedGroup['name']}
            label={`${feedGroup['name']} (${cowPercentage}%)`}
            value={formatNumber(cowsNrInRelToDaysGroup, 0)}
            unit={'Tiere'}
            max={parseInt(watchCowsNumber) / parseInt(watchCalvingPeriod) * feedGroups[indexType].days}
            disabled={Boolean(feedGroup['disabled']) || index < 2}
            onValueChange={(value) => update(index, { ...feedGroup, days: getDaysFromCowsNumber(value) })}
            onLabelChange={(value) => update(index, { ...feedGroup, name: value })}
            handleDelete={() => handleDeleteFeedGroup(index)}
          />
        })}
      </Form.Group>
    </>
  }

  function renderAssignFeeds() {
    return <AssignFeedPage
      milkOptions={milkOptions}
    />
  }

  function renderAddNewGroup() {
    return <AddNewGroup
      handleClose={() => setIsFeedGroupModalOpen(false)}
      isOpen={isFeedGroupModalOpen}
      handleSubmit={(data) => addNewFoodGroup(data)}
      max={parseInt(watchCowsNumber) / parseInt(watchCalvingPeriod) * feedGroups[1].days}
      maxTrockenCows={formatNumber(parseInt(watchCowsNumber) / parseInt(watchCalvingPeriod) * feedGroups[0].days, 0)}
      disableDryPeriodSplit={feedGroups.filter(feedGroup => feedGroup?.type === 1 && feedGroup?.disabled !== 1).length > 0}
    />
  }

  function renderPhaseFeeding() {
    return <Phases />
  }

  function renderAssingPhaseModal() {
    return <AssignPhasesModal />
  }

  return <Form.Field style={{ margin: '2em', marginTop: 0 }}>
    {renderAddNewGroup()}
    {feedPage === 0 && <>
      {renderFeedGroups()}
      {renderPhaseFeeding()}
    </>}
    {renderAssingPhaseModal()}
    {feedPage === 2 && renderAssignFeeds()}
  </Form.Field>
}
export default FeedGroups
